<script lang="ts" setup>
import { storeToRefs } from "pinia";
import { ref } from "vue";
import { useAcl } from "vue-simple-acl";
import type { TabNavigation } from "~/core/components/TabsWithNavigation.vue";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import { OrganizationType } from "~/modules/organization/models/OrganizationType.enum";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";
import {
  PATH_USER_MANAGEMENT_GROUPS,
  PATH_USER_MANAGEMENT_USERS,
} from "~/modules/user/constants";
import { UserColumns } from "~/modules/user/models/UserColumns.enum";
import ILxcPlus from "~icons/lxc/plus";

const { t } = useI18n();
const route = useRoute();
const { currentOrganization } = storeToRefs(useOrganizationStore());
const { can } = useAcl();

const showUserForm: Ref<boolean> = ref(false);
const currentSelectedUser: Ref<string | undefined> = ref(undefined);
const showGroupForm: Ref<boolean> = ref(false);
const currentSelectedGroup: Ref<string | undefined> = ref(undefined);

async function onCreateUser() {
  currentSelectedUser.value = undefined;
  showUserForm.value = true;
}

async function onCreateUserGroup() {
  currentSelectedGroup.value = undefined;
  showGroupForm.value = true;
}

function setForm() {
  const userLogin: string = route.params.userLogin as string;
  const groupCode: string = route.params.code as string;
  showUserForm.value = !!userLogin;
  showGroupForm.value = !!groupCode;
  if (userLogin) {
    currentSelectedUser.value = userLogin;
  } else if (groupCode) {
    currentSelectedGroup.value = groupCode as string;
  }
}

//
// Navigation section. Logic / configuration responsible for the navigation.
//
const usersTabId = "users";
const groupsTabId = "groups";

const userTabRedirectPath = PATH_USER_MANAGEMENT_USERS;

const tabNavigations: TabNavigation[] = [
  {
    id: usersTabId,
    redirectPath: userTabRedirectPath,
    labelTranslationKey: "tab.users.label",
    forceRefresh: true,
  },
  {
    id: groupsTabId,
    redirectPath: PATH_USER_MANAGEMENT_GROUPS,
    labelTranslationKey: "tab.userGroups.label",
    forceRefresh: true,
  },
];

// The current tab index is stored here as it is needed to display buttons above the tab components.
const selectedTabIndex: Ref<number | undefined> = ref();

const isUsersTabSelected = computed(
  (): boolean =>
    selectedTabIndex.value ===
    tabNavigations.findIndex(
      (tabNavigation) => tabNavigation.id === usersTabId,
    ),
);
const showUserAdd: ComputedRef<boolean> = computed((): boolean => {
  return (
    isUsersTabSelected.value &&
    can(ACLRoles.CAN_WRITE_USER) &&
    !currentOrganization.value?.useExternalIdp
  );
});
const isGroupsTabSelected = computed(
  (): boolean =>
    selectedTabIndex.value ===
    tabNavigations.findIndex(
      (tabNavigation) => tabNavigation.id === groupsTabId,
    ),
);
const showGroupAdd: ComputedRef<boolean> = computed((): boolean => {
  return isGroupsTabSelected.value && can(ACLRoles.CAN_WRITE_USER);
});

watch(route, setForm);

onMounted(setForm);
</script>

<template>
  <page-component :breadcrumbs="[{ title: t('layout.menu.users') }]">
    <template #top-right>
      <div v-if="showUserAdd" class="flex justify-end gap-4">
        <lxc-button
          :icon="ILxcPlus"
          :title="t('user.button.create')"
          class="self-center"
          html-type="button"
          type="primary"
          @click="onCreateUser"
        >
          {{ t("user.button.create") }}
        </lxc-button>
      </div>
      <div v-else-if="showGroupAdd">
        <lxc-button
          :icon="ILxcPlus"
          :title="t('userGroup.button.create')"
          class="self-center"
          html-type="button"
          type="primary"
          @click="onCreateUserGroup"
        >
          {{ t("userGroup.button.create") }}
        </lxc-button>
      </div>
    </template>

    <tabs-with-navigation
      :tab-navigations="tabNavigations"
      @update:selected-tab-index="
        (newSelectedTabIndex) => (selectedTabIndex = newSelectedTabIndex)
      "
    >
      <template #[usersTabId]>
        <user-list
          v-model:show-user-form="showUserForm"
          v-model:current-selected-user="currentSelectedUser"
          :organization-code="currentOrganization?.code"
          :columns="[
            UserColumns.LAST_NAME,
            UserColumns.FIRST_NAME,
            UserColumns.PROFILE,
            UserColumns.EMAIL,
            UserColumns.STATUS,
            UserColumns.GROUPS,
            UserColumns.ACTIONS,
          ]"
          :organization-type="OrganizationType.OPERATOR"
          :path-context="userTabRedirectPath"
          :use-external-idp="currentOrganization?.useExternalIdp"
          use-query-parameters-for-pagination
        />
      </template>

      <template #[groupsTabId]>
        <user-group-list
          v-model:show-group-form="showGroupForm"
          :current-selected-group="currentSelectedGroup"
        />
      </template>
    </tabs-with-navigation>
  </page-component>
</template>
